<template>
  <q-card
    v-touch-hold.mouse="onHoldAddBehavior"
    class="col-auto column full-height noselect"
    :class="{
      'bg-primary': isSelected,
      'text-white': isSelected,
      'cursor-pointer': targetIsOpen
    }"
    @click="onSelectTarget(target)"
  >
    <q-card-section
      :class="{
        'q-pb-none': !dense && !tiny,
        'q-pt-sm q-pb-none q-px-md text-overline text-uppercase text-dark-variant program-overline': tiny,
        'q-pt-xs q-pb-none': dense && !tiny,
      }"
    >
      <q-badge
        v-if="!!getTrialsPerTarget(target)"
        color="positive"
        floating
      >
        {{ getTrialsPerTarget(target) }}
      </q-badge>
      {{ target.label }}
    </q-card-section>
    <q-card-section
      class="text-center full-width text-subtitle2"
      :class="subTitleClass"
    >
      <q-chip
        size="sm"
        :style="getPromptLevelStyle(currentIntervalPromptLevel)"
      >
        {{ currentIntervalPromptLabel }}
      </q-chip>
    </q-card-section>
    <q-menu
      v-if="hasMultiplePromptLevelsAvailable && targetIsOpen"
      :anchor="promptLevelsMenuPosition"
      transition-show="none"
      transition-hide="none"
      auto-close
      :class="showAddBehavior ? 'prompt-levels-menu--detached' : ''"
      @hide="isSelected = false"
    >
      <q-list
        style="min-width: 100px"
        :class="showAddBehavior ? 'bg-white rounded-borders shadow-2' : ''"
      >
        <q-item
          v-for="promptLevel in availableProgramPrompts"
          :key="promptLevel.id"
          clickable
          :style="getPromptLevelStyle(promptLevel)"
          class="prompt-level"
          @click="onSetIntervalPromptLevel(promptLevel)"
        >
          <q-item-section>{{ promptLevel.trialOutcome }}</q-item-section>
        </q-item>
      </q-list>
      <add-behavior-occurrence-button
        v-if="showAddBehavior"
        @add="onAddBehaviorOccurrence"
      />
    </q-menu>
  </q-card>
</template>
<script>
import AddBehaviorOccurrenceButton from 'components/Programs/DataCollection/AddBehaviorOccurrenceButton';
import {
  chain,
  get, range, size,
} from 'lodash';
import { useBehaviorOccurrenceRecorder } from 'src/composables/useBehaviorOccurrenceRecorder';
import DataCollection from 'src/mixins/DataCollection';
import Targets from 'src/mixins/Targets';

export default {
  name: 'IntervalData',

  components: { AddBehaviorOccurrenceButton },

  mixins: [Targets, DataCollection],

  emits: ['record', 'record-program-data', 'record-multiple-data'],

  setup(props) {
    return useBehaviorOccurrenceRecorder(props);
  },

  data() {
    return {
      isSelected: false,
      existingTrials: new Set(),
      addedToExistingTrials: false,
    };
  },

  computed: {

    subTitleClass() {
      if (this.tiny) {
        return 'q-pt-none q-pb-none';
      }
      return this.dense ? 'q-py-none' : 'q-py-sm';
    },

    /**
     * Retrieves the effective settings for the current target.
     * If the target is part of a group (sub-target), it returns the parent target object.
     * If the target is a standalone unit (legacy structure), it falls back to the program object.
     * * Lookup Logic:
     * 1. Checks for the existence of parentTarget.
     * 2. Searches within the session's selectedTargets.
     * 3. Searches within the program's general target list.
     * 4. Defaults to the main program object if no parent is found.
     * * @returns {Object} The configuration object (either from a Parent Target or the Program).
     */
    parentTargetSettings() {
      const parentId = this.target?.parentTarget;
      if (!parentId) return this.sessionProgram.program || {};

      const selected = this.sessionProgram.selectedTargets;

      if (selected instanceof Map && selected.has(parentId)) {
        return selected.get(parentId);
      }

      if (selected && !Array.isArray(selected) && selected[parentId]) {
        return selected[parentId];
      }

      if (Array.isArray(selected)) {
        const found = selected.find((t) => t.id === parentId);
        if (found) return found;
      }

      const allProgramTargets = get(this.sessionProgram, 'program.targets', []);
      const foundInProgram = allProgramTargets.find((t) => t.id === parentId);

      if (foundInProgram) return foundInProgram;

      return {};
    }, // parentTargetSettings()

    /**
     * Resolves the current interval number from the data object.
     * Acts as a reactive bridge for both legacy (direct property)
     * and new (adjusted data from parent) interval structures.
     * @returns {number} The current interval number, defaulting to 1.
     */
    currentIntervalValue() {
      return get(this.data, 'currentInterval') || 1;
    }, // currentIntervalValue()

    /**
     * Overrides the mixin's programPrompts to support hierarchical settings.
     * It resolves the prompt levels template by looking at the parent target first,
     * and falling back to the global program template if needed.
     * Supports customized prompt levels if they are defined for the specific template.
     * @returns {Array} List of prompt level objects available for this target.
     */
    programPrompts() {
      const templateId = this.parentTargetSettings.defaultPromptLevelTemplate
        || this.parentTargetSettings.promptLevelTemplate;

      const template = this.activePromptLevels.find((t) => t.id === templateId)
        || this.programPromptLevelTemplate;

      const promptLevels = get(template, 'promptLevels', []);
      const customized = this.getCustomizedProgramPromptLevels(template);

      return customized
        ? this.applyCustomizedPromptLevels(promptLevels, customized)
        : promptLevels;
    }, // programPrompts()

    availableProgramPrompts() {
      return this.programPrompts.filter((prompt) => prompt.id !== this.currentIntervalPromptLevelId);
    },

    currentIntervalPromptLevelId() {
      const savedValue = get(this.data, `intervalData_${get(this.target, 'id', '')}`);
      const defaultPrompt = this.parentTargetSettings.defaultPromptLevel
        || this.sessionProgram.program.defaultPromptLevel;

      return savedValue ?? defaultPrompt;
    }, // currentIntervalPromptLevelId()

    currentIntervalPromptLevel() {
      const id = this.currentIntervalPromptLevelId;
      return this.programPrompts.find((prompt) => prompt.id === id);
    }, // currentIntervalPromptLevel()

    currentIntervalPromptLabel() {
      return get(this.currentIntervalPromptLevel, 'trialOutcome', '');
    }, // currentIntervalPromptLabel()

    defaultIntervalPromptLevel() {
      const id = this.parentTargetSettings.defaultPromptLevel;
      return this.programPrompts.find((prompt) => prompt.id === id);
    }, // defaultIntervalPromptLevel()

    hasMultiplePromptLevelsAvailable() {
      return size(this.availableProgramPrompts) !== 1;
    }, // hasMultiplePromptLevelsAvailable()

    promptLevelsMenuPosition() {
      if (this.$q.platform.is.ipad) {
        return 'bottom left';
      }

      return this.$q.screen.gt.sm ? 'top right' : undefined;
    }, // promptLevelsMenuPosition()

  },

  watch: {
    currentIntervalValue: {
      deep: true,
      immediate: false,
      handler(nextInterval, previousInterval) {
        if (!previousInterval || !nextInterval || (previousInterval >= nextInterval)) {
          return;
        }
        // in some cases, like after restoring from backup, the code may attempt to add already existing trials
        // using this to later filter them out
        this.addToExistingTrials();

        const recordInterval = nextInterval - 1;
        this.recordData(previousInterval);
        const defaultPromptLevel = this.parentTargetSettings?.defaultPromptLevel;
        if (previousInterval
          && defaultPromptLevel
          && (recordInterval >= (previousInterval + 1))
          && ((recordInterval - previousInterval) < 100)) {
          this.recordIntermediateData(previousInterval + 1, recordInterval);
        }

        if (get(this.data, `intervalData_${this.target.id}`) !== null) {
          this.recordIntervalPrompt(null);
        }
      },
    },
  },

  methods: {
    finishSession(finalInterval) {
      const defaultPromptLevel = get(this.sessionProgram, ['program', 'defaultPromptLevel']);
      const currentInterval = get(this.data, 'currentInterval') || 1;

      if (!this.targetIsOpen || !defaultPromptLevel || !finalInterval || finalInterval <= currentInterval) {
        return undefined;
      }

      this.addToExistingTrials();

      const records = [];

      if (this.currentIntervalPromptLevel && !this.existingTrials.has(currentInterval)) {
        this.existingTrials.add(currentInterval);
        records.push({
          target: this.target.id,
          promptLevel: this.currentIntervalPromptLevel.id,
          scoreValue: this.currentIntervalPromptLevel.score,
          trial: currentInterval,
        });
      }

      if (this.defaultIntervalPromptLevel) {
        for (let trial = currentInterval + 1; trial < finalInterval; trial += 1) {
          if (!this.existingTrials.has(trial)) {
            this.existingTrials.add(trial);
            records.push({
              target: this.target.id,
              promptLevel: this.defaultIntervalPromptLevel.id,
              scoreValue: this.defaultIntervalPromptLevel.score,
              trial,
            });
          }
        }
      }

      return records.length ? records : undefined;
    }, // finishSession()

    // A +/- template toggles on tap without a menu, so press-and-hold is the only way to the button.
    onHoldAddBehavior() {
      if (!this.showAddBehavior || !this.targetIsOpen) {
        return;
      }

      this.onAddBehaviorOccurrence();
    }, // onHoldAddBehavior()

    onSelectTarget() {
      if (!this.targetIsOpen) {
        return;
      }

      this.notifyDeletedPromptLevelTemplate();

      if (!this.hasMultiplePromptLevelsAvailable) {
        // Only one prompt level - record right away
        this.recordIntervalPrompt(this.availableProgramPrompts[0].id);
        this.isSelected = false;
        return;
      }

      this.isSelected = true;
    }, // onSelectTarget()

    onSetIntervalPromptLevel(promptLevel) {
      if (!this.targetIsOpen) {
        return;
      }
      this.recordIntervalPrompt(promptLevel.id);
      this.isSelected = false;
    }, // onSetIntervalPromptLevel()

    getTrialsPerTarget() {
      const trials = chain(this.data)
        .get('data')
        .filter({ target: get(this.target, 'id') })
        .value();

      const trialCount = size(trials);

      if (trialCount === 0) {
        return '';
      }

      if (!this.hasMultiplePromptLevelsAvailable) {
        return trialCount;
      }

      return this.calculateTrialsSummary(trials);
    }, // getTrialsPerTarget()

    recordData(currentInterval) {
      if (!this.currentIntervalPromptLevel || !this.targetIsOpen || this.existingTrials.has(currentInterval)) {
        return;
      }
      const data = {
        target: this.target.id,
        promptLevel: this.currentIntervalPromptLevel.id,
        scoreValue: this.currentIntervalPromptLevel.score,
        trial: currentInterval,
      };
      this.existingTrials.add(currentInterval);
      this.$emit('record', data);
    }, // recordData()

    addToExistingTrials() {
      if (!this.addedToExistingTrials && Array.isArray(this.data.data)) {
        this.addedToExistingTrials = true;
        this.data.data
          .filter((d) => d.target === this.target.id)
          .forEach((dataEntry) => this.existingTrials.add(dataEntry.trial));
      }
    }, // addToExistingTrials()

    recordIntervalPrompt(intervalPrompt) {
      if (!this.targetIsOpen) {
        return;
      }
      this.$emit('record-program-data', this.sessionProgram, { [`intervalData_${this.target.id}`]: intervalPrompt });
    }, // recordIntervalPrompt()

    recordIntermediateData(fromInterval, toInterval) {
      if (!this.targetIsOpen) {
        return;
      }

      // Remember that toInterval is excluded from "range"
      const dataRangeCollection = range(fromInterval, toInterval + 1)
        .filter((trialNum) => !this.existingTrials.has(trialNum))
        .map((trial) => {
          this.existingTrials.add(trial);
          return {
            target: this.target.id,
            promptLevel: this.defaultIntervalPromptLevel.id,
            scoreValue: this.defaultIntervalPromptLevel.score,
            trial,
          };
        });

      this.$emit('record-multiple-data', dataRangeCollection);
    }, // recordIntermediateData()

  },

};
</script>
<style scoped>

.prompt-level {
  border-width: 0px 5px;
  border-style: solid;
}

.program-overline {
  font-size: 7px;
  line-height:1rem;
}
</style>
